// Pure canvas drawing helpers. Every function takes a 2D context as its first
// argument and has no external state — so these are trivially testable.
import {
  W,
  H,
  PADDLE_Y,
  PADDLE_H,
  BALL_RADIUS,
  TIER_COLORS,
  TIER_GLOW,
} from "./constants";
import { drawComet, DEFAULT_SKIN } from "../../lib/comet";

export function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

export function drawBackground(ctx, s) {
  ctx.fillStyle = "#05050a";
  ctx.fillRect(0, 0, W, H);
  // Deep-violet radial wash from the bottom — same as before, just a hair
  // richer to match the Comet reference background.
  const g = ctx.createRadialGradient(W / 2, H * 0.7, 10, W / 2, H * 0.7, H * 0.95);
  g.addColorStop(0, "rgba(155,33,255,0.10)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  // Drifting starfield — slightly denser + 2-tier brightness for depth.
  s.bgOffset = (s.bgOffset + 0.3) % H;
  for (let i = 0; i < 80; i++) {
    const seed = i * 137.5;
    const x = (seed * 13) % W;
    const y = (seed * 7 + s.bgOffset) % H;
    const isBright = i % 4 === 0;
    const r = isBright ? 1.5 : 0.7;
    ctx.fillStyle = isBright ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.55)";
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Signature comet streak across the lower-right — matches the Comet
  // reference background. Slow drift across H so it feels alive without
  // distracting from gameplay.
  const cometT = (performance.now() * 0.00008) % 1;
  const ccx = W * (0.15 + cometT * 0.7);
  const ccy = H * 0.78;
  ctx.save();
  ctx.translate(ccx, ccy);
  ctx.rotate(-0.35);
  ctx.globalCompositeOperation = "lighter";
  const cgrad = ctx.createLinearGradient(-W * 0.45, 0, W * 0.05, 0);
  cgrad.addColorStop(0,    "rgba(155,33,255,0)");
  cgrad.addColorStop(0.55, "rgba(155,33,255,0.18)");
  cgrad.addColorStop(0.85, "rgba(255,61,240,0.32)");
  cgrad.addColorStop(1,    "rgba(255,255,255,0.55)");
  ctx.fillStyle = cgrad;
  ctx.beginPath();
  ctx.ellipse(-W * 0.2, 0, W * 0.25, 4.5, 0, 0, Math.PI * 2);
  ctx.fill();
  // Bright sparkle head at the leading edge
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.beginPath();
  ctx.arc(W * 0.05, 0, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Outer cyan border (kept — anchors the play area)
  ctx.strokeStyle = "rgba(0,255,255,0.35)";
  ctx.lineWidth = 1;
  ctx.strokeRect(10, 10, W - 20, H - 20);
}

export function drawBlock(ctx, b) {
  ctx.save();
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  ctx.translate(cx, cy);
  if (b.tier === 4) {
    // Tier-4 bosses still pulse subtly so they read as "alive" — but
    // no rotation. Brick rotation was retired in May 2026 in favour
    // of static space-shape level layouts.
    const pulse = 1 + Math.sin(performance.now() * 0.006) * 0.06;
    ctx.scale(pulse, pulse);
  }
  const color = TIER_COLORS[b.tier];
  const glow = TIER_GLOW[b.tier];
  const r = 7;
  const halfW = b.w / 2;
  const halfH = b.h / 2;
  // Outer glow halo — soft, generous so each brick reads as a luminous gem.
  ctx.shadowColor = glow;
  ctx.shadowBlur = b.tier === 4 ? 30 : 22;
  ctx.fillStyle = color;
  roundRect(ctx, -halfW, -halfH, b.w, b.h, r);
  ctx.fill();
  ctx.shadowBlur = 0;
  // Body gradient — vertical lighter→darker tint on the brick body. Gives
  // each tier the rounded "gem" shading from the reference art.
  const grad = ctx.createLinearGradient(0, -halfH, 0, halfH);
  grad.addColorStop(0, glow);
  grad.addColorStop(0.55, color);
  grad.addColorStop(1, color);
  ctx.fillStyle = grad;
  roundRect(ctx, -halfW + 1.5, -halfH + 1.5, b.w - 3, b.h - 3, r - 1.5);
  ctx.fill();
  // Sparkle starfield — 3-7 tiny white dots scattered deterministically by
  // the brick's grid position so the pattern is stable per brick (no flicker
  // and identical for the same level seed).
  ctx.save();
  ctx.beginPath();
  roundRect(ctx, -halfW + 2, -halfH + 2, b.w - 4, b.h - 4, r - 2);
  ctx.clip();
  const seed = Math.abs(Math.sin(b.x * 0.13 + b.y * 0.27)) % 1;
  const sparkCount = b.tier === 4 ? 7 : 5;
  for (let i = 0; i < sparkCount; i++) {
    const sx = -halfW + ((seed * 13.37 + i * 7.91) % 1) * b.w;
    const sy = -halfH + ((seed * 31.71 + i * 3.13) % 1) * b.h;
    const sr = 0.4 + (i % 3) * 0.35;
    ctx.fillStyle = `rgba(255,255,255,${0.55 + (i % 3) * 0.12})`;
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.fill();
  }
  // Diagonal comet streak — single bright sweep across the brick body
  // (the signature element of the reference Comet art).
  const streakAngle = -0.55; // slight upward slope
  const streakOffset = ((seed * 0.5) - 0.25) * b.w;
  ctx.translate(streakOffset, 0);
  ctx.rotate(streakAngle);
  const sg = ctx.createLinearGradient(-halfW, 0, halfW, 0);
  sg.addColorStop(0, "rgba(255,255,255,0)");
  sg.addColorStop(0.45, "rgba(255,255,255,0.5)");
  sg.addColorStop(0.7, "rgba(255,255,255,0.85)");
  sg.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = sg;
  ctx.beginPath();
  ctx.ellipse(0, 0, b.w * 0.7, 1.3, 0, 0, Math.PI * 2);
  ctx.fill();
  // Bright sparkle head at the leading edge of the streak
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.beginPath();
  ctx.arc(b.w * 0.55, 0, 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Top specular highlight band — sells the rounded gem feel.
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  roundRect(ctx, -halfW + 4, -halfH + 3, b.w - 8, Math.max(2, b.h * 0.22), r - 3);
  ctx.fill();
  // Tier-4 boss star indicator (kept from previous design)
  if (b.tier === 4) {
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.font = "bold 12px 'JetBrains Mono', monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("★", 0, 1);
  }
  // HP pip strip (multi-hit bricks) — readable on the new busier brick face.
  if (b.hp > 1) {
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    for (let i = 0; i < b.hp; i++) {
      ctx.fillRect(-halfW + 5 + i * 5, halfH - 6, 3, 2);
    }
  }
  ctx.restore();
}

// Light/dark shade helpers for computing gradient stops from a single accent hex
function hexToRgb(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}
function rgbToHex(r, g, b) {
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${clamp(r).toString(16).padStart(2, "0")}${clamp(g).toString(16).padStart(2, "0")}${clamp(b).toString(16).padStart(2, "0")}`;
}
function lighten(hex, amt) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
}
function darken(hex, amt) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(r * (1 - amt), g * (1 - amt), b * (1 - amt));
}

export function skinColors(activeSkin, accent) {
  // If the user has picked a paddle-accent override, derive glow / from / to
  // stops from the single colour — works regardless of which named skin is
  // equipped, including `rainbow` / `legendary-ascended`.
  if (accent) {
    return {
      glow: accent,
      from: lighten(accent, 0.45),
      to: darken(accent, 0.55),
      eyeBg: "#fff",
    };
  }
  if (activeSkin === "gold") return { glow: "#ffd700", from: "#fff4a1", to: "#b88400", eyeBg: "#fff" };
  if (activeSkin === "rainbow") {
    const t = (performance.now() * 0.0015) % 1;
    const hue = Math.floor(t * 360);
    return {
      glow: `hsl(${hue}, 100%, 60%)`,
      from: `hsl(${hue}, 100%, 70%)`,
      to: `hsl(${(hue + 60) % 360}, 100%, 45%)`,
      eyeBg: "#fff",
    };
  }
  if (activeSkin === "beginner-victor") return { glow: "#00ffff", from: "#a0ffff", to: "#0080b3", eyeBg: "#fff" };
  if (activeSkin === "easy-champion") return { glow: "#00ff41", from: "#a0ffb5", to: "#007a26", eyeBg: "#fff" };
  if (activeSkin === "medium-conqueror") return { glow: "#ffd700", from: "#fff2a0", to: "#805f00", eyeBg: "#05050a" };
  if (activeSkin === "hard-warrior") return { glow: "#ff8c1a", from: "#ffc78a", to: "#7a3b00", eyeBg: "#fff" };
  if (activeSkin === "expert-master") return { glow: "#ff007f", from: "#ff80bf", to: "#800040", eyeBg: "#fff" };
  if (activeSkin === "legendary-ascended") {
    const t = (performance.now() * 0.002) % 1;
    const hue = 270 + Math.sin(t * Math.PI * 2) * 40;
    return {
      glow: `hsl(${hue}, 100%, 65%)`,
      from: `hsl(${hue}, 100%, 80%)`,
      to: `hsl(${hue - 30}, 100%, 30%)`,
      eyeBg: "#fff",
    };
  }
  if (activeSkin === "halloween-pumpkin") {
    // Jack-o-lantern palette — pumpkin orange glow, warm cream mid,
    // deep-crimson stem shadow. The special-cased renderer draws the
    // pumpkin face on top of the pill in drawPaddle.
    return { glow: "#ff6a00", from: "#ffb648", to: "#4a1a00", eyeBg: "#0a0500" };
  }
  return { glow: "#b026ff", from: "#d462ff", to: "#7a1fb8", eyeBg: "#fff" };
}

export function drawPaddle(ctx, p, activeSkin, accent = null) {
  ctx.save();
  const x = p.x;
  const y = PADDLE_Y;
  const w = p.w;
  const h = PADDLE_H + 6;
  const sc = skinColors(activeSkin, accent);
  // Outer glow halo — softer, wider than before to match the comet aura
  // wrapping the reference paddle.
  ctx.shadowColor = sc.glow;
  ctx.shadowBlur = 28;
  // Cosmic body gradient — left (deep violet) → right (bright magenta)
  // matches the reference paddle exactly. Driven by skin colors so every
  // ball-skin's paddle accent blends consistently.
  const bodyG = ctx.createLinearGradient(x, y, x + w, y);
  bodyG.addColorStop(0,    "#1a0033");        // deep cosmic indigo
  bodyG.addColorStop(0.35, sc.glow);          // mid violet (skin-tinted)
  bodyG.addColorStop(0.7,  sc.from);          // brighter magenta/pink
  bodyG.addColorStop(1,    "#ffffff");        // white-hot leading edge
  ctx.fillStyle = bodyG;
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  // Vertical body shading overlay — gives the pill its rounded gem feel.
  ctx.save();
  ctx.beginPath();
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.clip();
  const shade = ctx.createLinearGradient(0, y, 0, y + h);
  shade.addColorStop(0,    "rgba(255,255,255,0.18)");
  shade.addColorStop(0.45, "rgba(255,255,255,0.0)");
  shade.addColorStop(1,    "rgba(0,0,0,0.32)");
  ctx.fillStyle = shade;
  ctx.fillRect(x, y, w, h);
  // Sparkle starfield scattered across the paddle body — signature
  // element of the reference art. Density grows toward the leading edge
  // so the right side reads as more energetic. Seeded by paddle width so
  // the pattern is stable per-frame (no flicker).
  const seedBase = Math.floor(x * 0.13 + w * 0.7);
  for (let i = 0; i < 18; i++) {
    const r1 = ((seedBase + i * 71) % 100) / 100;        // 0..1 across width
    const r2 = ((seedBase + i * 137) % 100) / 100;       // 0..1 across height
    const r3 = ((seedBase + i * 211) % 100) / 100;       // size jitter
    // Bias placement toward the leading edge (right side)
    const px = x + w * Math.pow(r1, 0.7);
    const py = y + h * (0.15 + r2 * 0.7);
    const ps = 0.5 + r3 * 1.3;
    const alpha = 0.4 + (px - x) / w * 0.55;
    ctx.fillStyle = `rgba(255,255,255,${alpha})`;
    ctx.beginPath();
    ctx.arc(px, py, ps, 0, Math.PI * 2);
    ctx.fill();
  }
  // Bright comet streak across the body — clipped to the pill.
  const streakG = ctx.createLinearGradient(x, y + h / 2, x + w, y + h / 2);
  streakG.addColorStop(0,    "rgba(255,255,255,0)");
  streakG.addColorStop(0.55, "rgba(255,255,255,0.25)");
  streakG.addColorStop(0.85, "rgba(255,255,255,0.7)");
  streakG.addColorStop(1,    "rgba(255,255,255,0.95)");
  ctx.fillStyle = streakG;
  ctx.beginPath();
  ctx.ellipse(x + w * 0.55, y + h / 2 - 1, w * 0.45, h * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();
  // Bright sparkle bulb at the leading edge — the white-hot focal point
  // from the reference art.
  ctx.fillStyle = "rgba(255,255,255,1)";
  ctx.beginPath();
  ctx.arc(x + w - 5, y + h / 2, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.beginPath();
  ctx.arc(x + w - 5, y + h / 2, 6.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Top specular highlight — a hair narrower since the gradient now
  // carries most of the rounded shading.
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  roundRect(ctx, x + 6, y + 2, w - 12, h * 0.26, 4);
  ctx.fill();
  // Worm eyes at the leading edge — reads as the paddle "face"
  const eyeCx = x + w - 14;
  const eyeCy = y + h / 2 - 2;
  ctx.fillStyle = sc.eyeBg;
  ctx.beginPath();
  ctx.arc(eyeCx, eyeCy, 4, 0, Math.PI * 2);
  ctx.arc(eyeCx - 10, eyeCy, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#05050a";
  ctx.beginPath();
  ctx.arc(eyeCx + 1, eyeCy + 0.5, 2, 0, Math.PI * 2);
  ctx.arc(eyeCx - 9, eyeCy + 0.5, 2, 0, Math.PI * 2);
  ctx.fill();
  // Halloween Pumpkin paddle overlay — triangle jack-o-lantern eyes,
  // curved stem, and serrated grin painted on top of the pill body.
  // Only activates when the halloween-pumpkin paddle skin is equipped.
  if (activeSkin === "halloween-pumpkin") {
    ctx.save();
    // Pumpkin vertical ridges — thin dark arcs down the pill body
    ctx.strokeStyle = "rgba(74, 26, 0, 0.55)";
    ctx.lineWidth = 1.1;
    for (let i = 1; i < 5; i++) {
      const rx = x + (w * i) / 5;
      ctx.beginPath();
      ctx.ellipse(rx, y + h / 2, 3, h * 0.42, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    // Stem — small green nub on top-left
    ctx.fillStyle = "#3d8033";
    ctx.beginPath();
    ctx.ellipse(x + w * 0.18, y - 2, 4, 5, -0.2, 0, Math.PI * 2);
    ctx.fill();
    // Jack-o-lantern eyes — two triangles glowing yellow, centered on paddle
    ctx.fillStyle = "#ffe066";
    ctx.shadowColor = "#ffb648";
    ctx.shadowBlur = 8;
    const eyeSize = h * 0.28;
    const midY = y + h / 2 - 2;
    const eyeXA = x + w * 0.42;
    const eyeXB = x + w * 0.58;
    ctx.beginPath();
    ctx.moveTo(eyeXA, midY - eyeSize / 2);
    ctx.lineTo(eyeXA - eyeSize / 1.4, midY + eyeSize / 2);
    ctx.lineTo(eyeXA + eyeSize / 1.4, midY + eyeSize / 2);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(eyeXB, midY - eyeSize / 2);
    ctx.lineTo(eyeXB - eyeSize / 1.4, midY + eyeSize / 2);
    ctx.lineTo(eyeXB + eyeSize / 1.4, midY + eyeSize / 2);
    ctx.closePath();
    ctx.fill();
    // Serrated jack-o-lantern mouth — small zigzag along the pill bottom
    ctx.shadowBlur = 4;
    ctx.strokeStyle = "#ffe066";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    const mouthY = y + h * 0.78;
    const mouthLeft = x + w * 0.34;
    const mouthRight = x + w * 0.66;
    const teeth = 5;
    ctx.moveTo(mouthLeft, mouthY);
    for (let i = 1; i <= teeth * 2; i++) {
      const tx = mouthLeft + ((mouthRight - mouthLeft) * i) / (teeth * 2);
      const ty = mouthY + (i % 2 === 0 ? 0 : 3);
      ctx.lineTo(tx, ty);
    }
    ctx.stroke();
    ctx.restore();
  }
  ctx.restore();
}

export function drawWorm(ctx, b, skin) {
  // Delegate to the shared comet renderer so the equipped ball skin is
  // visible in-game (the same renderer powers the /skins preview card).
  const angle = Math.atan2(b.vy, b.vx);
  drawComet(ctx, skin || DEFAULT_SKIN, b.x, b.y, BALL_RADIUS, {
    angle,
    trail: b.trail,
  });
}

// Power-up palette + icon glyphs — matches the Comet reference art.
// Each drop is drawn as a vertical teardrop comet with a central icon
// rather than the legacy flat label pill. `icon` is a single-character
// glyph drawn over the bulb; for the more bespoke icons (multi, expand)
// the renderer overrides with a drawn shape.
const POWERUP_META = {
  multi:   { color: "#cf95ff", glow: "#9b51ff", icon: "•••",      label: "MULTI"  },
  enlarge: { color: "#ff9af3", glow: "#ff3df0", icon: "↔",       label: "WIDE"   },
  slow:    { color: "#9be4ff", glow: "#3aa8ff", icon: "◷",       label: "SLOW"   },
  life:    { color: "#ffe49a", glow: "#ffb648", icon: "♥",       label: "LIFE"   },
  warp:    { color: "#cf95ff", glow: "#b026ff", icon: "✦",       label: "WARP"   },
  hole:    { color: "#a78bfa", glow: "#6b3df5", icon: "◉",       label: "HOLE"   },
  // Blood-Red Stingray Swarm — boss-killer power-up (3 stingrays).
  // Translucent crimson teardrop body with a tiny manta silhouette icon.
  stingray:{ color: "#ff5577", glow: "#ff1f3a", icon: "≈",       label: "STING"  },
};

export function drawPowerup(ctx, p) {
  const meta = POWERUP_META[p.kind];
  ctx.save();
  // Teardrop comet body — bulb at the bottom (where the gameplay icon sits)
  // tapering up into a long flame trail. Drawn around (p.x, p.y) which is
  // the bulb center.
  const bulbR = 13;
  const tailLen = 26;
  const cx = p.x;
  const cy = p.y;
  ctx.shadowColor = meta.glow;
  ctx.shadowBlur = 18;
  const bodyG = ctx.createRadialGradient(cx, cy, 1, cx, cy, bulbR + 6);
  bodyG.addColorStop(0,    "#ffffff");
  bodyG.addColorStop(0.45, meta.color);
  bodyG.addColorStop(1,    meta.glow);
  ctx.fillStyle = bodyG;
  ctx.beginPath();
  // Custom teardrop path — bulb circle + curved tail tapering up
  ctx.moveTo(cx - bulbR, cy);
  ctx.bezierCurveTo(cx - bulbR, cy - bulbR, cx - bulbR * 0.5, cy - tailLen, cx, cy - tailLen - 2);
  ctx.bezierCurveTo(cx + bulbR * 0.5, cy - tailLen, cx + bulbR, cy - bulbR, cx + bulbR, cy);
  ctx.arc(cx, cy, bulbR, 0, Math.PI);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  // Inner ring outline so the icon reads against the bright bulb interior.
  ctx.strokeStyle = meta.glow;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.arc(cx, cy, bulbR - 1, 0, Math.PI * 2);
  ctx.stroke();
  // Icon — drawn shape per kind (more legible than glyph fonts at gameplay scale)
  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  if (p.kind === "multi") {
    // 3 small balls in a triangle
    [[-4.5, 1.5], [4.5, 1.5], [0, -4]].forEach(([dx, dy]) => {
      ctx.beginPath();
      ctx.arc(cx + dx, cy + dy, 2.1, 0, Math.PI * 2);
      ctx.fill();
    });
  } else if (p.kind === "enlarge") {
    // Two arrows pointing outward
    ctx.beginPath();
    ctx.moveTo(cx - 6, cy);
    ctx.lineTo(cx + 6, cy);
    ctx.stroke();
    // arrowhead L
    ctx.beginPath();
    ctx.moveTo(cx - 6, cy);
    ctx.lineTo(cx - 3, cy - 3);
    ctx.moveTo(cx - 6, cy);
    ctx.lineTo(cx - 3, cy + 3);
    ctx.stroke();
    // arrowhead R
    ctx.beginPath();
    ctx.moveTo(cx + 6, cy);
    ctx.lineTo(cx + 3, cy - 3);
    ctx.moveTo(cx + 6, cy);
    ctx.lineTo(cx + 3, cy + 3);
    ctx.stroke();
  } else if (p.kind === "slow") {
    // Clock face with hands
    ctx.beginPath();
    ctx.arc(cx, cy, 5.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx, cy - 4);
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + 3, cy + 1);
    ctx.stroke();
  } else if (p.kind === "life") {
    // Heart
    ctx.beginPath();
    ctx.moveTo(cx, cy + 5);
    ctx.bezierCurveTo(cx - 8, cy - 1, cx - 5, cy - 7, cx, cy - 3);
    ctx.bezierCurveTo(cx + 5, cy - 7, cx + 8, cy - 1, cx, cy + 5);
    ctx.closePath();
    ctx.fill();
  } else if (p.kind === "warp") {
    // 4-point star
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const ang = (i / 8) * Math.PI * 2 - Math.PI / 2;
      const r = i % 2 === 0 ? 6 : 2.4;
      const x = cx + Math.cos(ang) * r;
      const y = cy + Math.sin(ang) * r;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  } else if (p.kind === "hole") {
    // Bullseye
    ctx.beginPath();
    ctx.arc(cx, cy, 5.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.fill();
  } else if (p.kind === "stingray") {
    // Tiny stingray silhouette — manta-ray diamond shape pointing up.
    ctx.beginPath();
    ctx.moveTo(cx, cy - 5.5);          // nose (top)
    ctx.quadraticCurveTo(cx - 4, cy - 2, cx - 6.5, cy + 2);  // left wing tip
    ctx.quadraticCurveTo(cx - 2, cy + 1, cx, cy + 4.5);      // tail-side cusp
    ctx.quadraticCurveTo(cx + 2, cy + 1, cx + 6.5, cy + 2);  // tail-side right
    ctx.quadraticCurveTo(cx + 4, cy - 2, cx, cy - 5.5);      // right wing → nose
    ctx.closePath();
    ctx.fill();
    // Whip tail.
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(cx, cy + 4.5);
    ctx.lineTo(cx, cy + 7.5);
    ctx.stroke();
  }
  // Bright sparkle at the very tail tip
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.beginPath();
  ctx.arc(cx, cy - tailLen - 2, 2.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Endless-mode moonstone pickup — opal crescent with iridescent sheen. A
// rim of cyan/violet fringes the bright pearl core; a dark "negative space"
// arc carves out the crescent. Caller passes a `phase` (0..1) for soft
// rotation/sparkle so a field of stones doesn't look static. The optional
// `palette` overrides colours so a pilot's owned moonstone skin can recolour
// every stone consistently across the field.
function _hexToRgb(hex) {
  if (typeof hex !== "string") return null;
  let h = hex.trim();
  if (h.startsWith("#")) h = h.slice(1);
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function _rgbaFrom(hex, alpha) {
  const rgb = _hexToRgb(hex);
  if (!rgb) return `rgba(192,132,252,${alpha})`;
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
}

export function drawMoonstone(ctx, m, palette) {
  const t = (performance.now() / 1000 + (m.seed || 0)) % (Math.PI * 2);
  // Clamp to a sane minimum — the rim/sparkle math subtracts up to 3 from
  // `r`, so a tiny moonstone (e.g., a collect-animation shrink-out frame)
  // would otherwise pass a negative radius to ctx.arc and crash the loop
  // with `IndexSizeError: radius is negative`.
  const r = Math.max(3.1, m.r || 11);
  const x = m.x;
  const y = m.y;
  const p = palette || {
    core: "#ffffff",
    mid: "#c084fc",
    halo: "#3c1e78",
    rim_a: "#7dffff",
    rim_b: "#ff78dc",
  };
  ctx.save();
  ctx.shadowColor = p.mid;
  ctx.shadowBlur = 14;
  // Pearl core (radial opal — three stops directly from the palette so the
  // skin colour fully owns the gradient. Earlier we hardcoded "#f3e8ff" at
  // 0.35 which bled lavender into every non-violet skin and made gold and
  // sapphire moonstones still read as purple. Now: core → core-mid mix →
  // mid → halo, derived from the palette.
  const grad = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, 1, x, y, r);
  grad.addColorStop(0, p.core);
  // Soft transition stop blended from core+mid so the inner glow takes on
  // the skin tint without losing its bright pearlescent center.
  grad.addColorStop(0.4, _rgbaFrom(p.mid, 0.7));
  grad.addColorStop(0.75, p.mid);
  grad.addColorStop(1, p.halo);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  // Crescent — punch out an offset dark arc to reveal the moon shape.
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.arc(x + r * 0.45, y - r * 0.1, r * 0.85, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";
  // Iridescent rim — additive coloured fringe in the skin's two accent
  // colours. Use rgba() instead of 8-char hex so older canvases don't
  // mis-render the alpha. Brightness pulses on `t` to keep the field alive.
  ctx.globalCompositeOperation = "lighter";
  const rimAlphaA = 0.45 + 0.25 * Math.sin(t);
  const rimAlphaB = 0.35 + 0.2 * Math.cos(t * 1.3);
  ctx.strokeStyle = _rgbaFrom(p.rim_a, rimAlphaA);
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(x, y, r - 1, -Math.PI * 0.6, Math.PI * 0.4);
  ctx.stroke();
  ctx.strokeStyle = _rgbaFrom(p.rim_b, rimAlphaB);
  ctx.beginPath();
  ctx.arc(x, y, r - 2.5, Math.PI * 0.2, Math.PI * 1.1);
  ctx.stroke();
  ctx.globalCompositeOperation = "source-over";
  // Tiny twinkle sparkle that drifts around the crescent edge
  const sx = x + Math.cos(t) * (r - 3);
  const sy = y + Math.sin(t) * (r - 3);
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.beginPath();
  ctx.arc(sx, sy, 1.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Plasma bolt — bright vertical projectile fired from the paddle when the
// Plasma Shot moonstone power-up is active. Travels straight up, destroys
// the first block it overlaps. Renders as a short tracer with a fading tail.
export function drawPlasmaBolt(ctx, p) {
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.shadowColor = "#ff66ff";
  ctx.shadowBlur = 14;
  const len = 22;
  const grad = ctx.createLinearGradient(p.x, p.y - len, p.x, p.y + 4);
  grad.addColorStop(0, "rgba(255,102,255,0)");
  grad.addColorStop(0.6, "rgba(255,160,255,0.85)");
  grad.addColorStop(1, "#ffffff");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.ellipse(p.x, p.y - len / 2 + 2, 2.6, len / 2 + 4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.beginPath();
  ctx.arc(p.x, p.y, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Full-screen ambient overlays for the moonstone power-ups. Renders AFTER
// every other draw call so the tint layers on top of the field but below
// React HUD chips. `fxState` carries `{ phaseActive, dilationActive,
// plasmaActive }` booleans + a `t` (seconds) so animated effects (Plasma
// scanlines, Dilation breathing border) drift coherently.
export function drawFxOverlays(ctx, w, h, fx) {
  if (!fx || (!fx.phaseActive && !fx.dilationActive && !fx.plasmaActive)) return;
  const t = fx.t || 0;
  ctx.save();

  // Phase Ball — soft violet vignette: a radial gradient that's transparent
  // at the centre and ~10% violet at the corners. Reads as a "dimensional
  // shimmer" without obscuring the field.
  if (fx.phaseActive) {
    const cx = w / 2;
    const cy = h / 2;
    const grad = ctx.createRadialGradient(cx, cy, w * 0.25, cx, cy, w * 0.75);
    grad.addColorStop(0, "rgba(167,139,250,0)");
    grad.addColorStop(0.6, "rgba(167,139,250,0.04)");
    grad.addColorStop(1, "rgba(167,139,250,0.18)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    // Faint violet inner ring to read as "phasing through reality".
    ctx.strokeStyle = `rgba(167,139,250,${0.18 + 0.08 * Math.sin(t * 4)})`;
    ctx.lineWidth = 2;
    ctx.strokeRect(4, 4, w - 8, h - 8);
  }

  // Time Dilation — cyan slow-mo border + breathing inner glow. Reads
  // immediately as "everything is in slow motion" because the border
  // pulse is tied to the in-game clock (so it pulses slowly).
  if (fx.dilationActive) {
    const pulse = 0.3 + 0.15 * Math.sin(t * 3);
    ctx.strokeStyle = `rgba(122,252,255,${pulse})`;
    ctx.lineWidth = 6;
    ctx.shadowColor = "#7afcff";
    ctx.shadowBlur = 22;
    ctx.strokeRect(3, 3, w - 6, h - 6);
    ctx.shadowBlur = 0;
    // Subtle horizontal scan-bar drifting downward (1.5px wide).
    const barY = ((t * 60) % h) | 0;
    ctx.fillStyle = "rgba(122,252,255,0.07)";
    ctx.fillRect(0, barY, w, 1.5);
  }

  // Plasma Shot — additive pink scanlines marching upward. Sits behind the
  // bricks visually because we draw with low alpha, but signals "the paddle
  // is ARMED with a gun" without much visual cost. Lines are ~14px apart.
  if (fx.plasmaActive) {
    ctx.globalCompositeOperation = "lighter";
    const offset = (t * 80) % 14;
    ctx.fillStyle = "rgba(255,102,255,0.05)";
    for (let y = -offset; y < h; y += 14) {
      ctx.fillRect(0, y, w, 1);
    }
    // Pink corner-bracket flourish, top-left + top-right + bottom corners
    ctx.strokeStyle = `rgba(255,102,255,${0.4 + 0.15 * Math.sin(t * 5)})`;
    ctx.lineWidth = 2;
    const c = 18;
    ctx.beginPath();
    // top-left
    ctx.moveTo(2, c); ctx.lineTo(2, 2); ctx.lineTo(c, 2);
    // top-right
    ctx.moveTo(w - c, 2); ctx.lineTo(w - 2, 2); ctx.lineTo(w - 2, c);
    // bottom-left
    ctx.moveTo(2, h - c); ctx.lineTo(2, h - 2); ctx.lineTo(c, h - 2);
    // bottom-right
    ctx.moveTo(w - c, h - 2); ctx.lineTo(w - 2, h - 2); ctx.lineTo(w - 2, h - c);
    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
  }

  ctx.restore();
}

// Activation flash — a 200ms full-screen pulse painted at the moment a
// moonstone power-up is purchased. Caller passes `{ color, until }` where
// `until` is a `gameNow()` timestamp; intensity ramps down linearly to 0
// over the lifetime so the flash reads as "fade out" rather than a snap-cut.
export function drawFxFlash(ctx, w, h, flash, now) {
  if (!flash || !flash.until || flash.until <= now) return;
  const lifeMs = 200;
  const t = Math.max(0, Math.min(1, (flash.until - now) / lifeMs));
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.fillStyle = `${flash.color}${Math.round(t * 70).toString(16).padStart(2, "0")}`;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

// Floating "+N ☾" tick spawned at the paddle when a moonstone is caught.
// Drifts upward, fading out over 600ms. Caller spawns one entry per catch
// and the loop pushes them through `state.stoneTicks`. Renders with the
// active moonstone-skin palette so the tick colour matches the in-field
// stone aesthetic. When `tick.gold` is set, the label reads "+N ☾" in
// gold instead of the palette colour — used by the streak multiplier.
export function drawStoneTicks(ctx, ticks, now, palette) {
  if (!ticks || ticks.length === 0) return;
  const lifeMs = 600;
  ctx.save();
  for (let i = ticks.length - 1; i >= 0; i--) {
    const tick = ticks[i];
    const age = now - tick.t0;
    if (age >= lifeMs) { ticks.splice(i, 1); continue; }
    const t = age / lifeMs;
    const y = tick.y - t * 28;            // drift 28px upward
    const alpha = 1 - t;                  // linear fade-out
    const color = tick.gold
      ? "#ffd700"
      : (palette && palette.mid) || "#c084fc";
    const label = `+${tick.amount || 1} ☾`;
    ctx.font = `bold ${tick.gold ? 15 : 13}px var(--font-mono, monospace)`;
    ctx.textAlign = "center";
    ctx.shadowColor = color;
    ctx.shadowBlur = tick.gold ? 12 : 8;
    ctx.fillStyle = `${color}${Math.round(alpha * 255).toString(16).padStart(2, "0")}`;
    ctx.fillText(label, tick.x, y);
  }
  ctx.restore();
}

// Comet Storm streaks — 400ms meteor-shower choreography painted BEFORE
// the 30% brick destruction fires. Each streak is a fading orange tracer
// that sweeps diagonally across the field. Caller passes an array of
// `{x1, y1, x2, y2, t0, life}` entries; expired entries are spliced by the
// helper so the loop stays simple.
export function drawCometStreaks(ctx, streaks, now) {
  if (!streaks || streaks.length === 0) return;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  for (let i = streaks.length - 1; i >= 0; i--) {
    const sk = streaks[i];
    const age = now - sk.t0;
    if (age >= sk.life) { streaks.splice(i, 1); continue; }
    const p = Math.min(1, age / sk.life);
    // Streak head position animates from (x1,y1) → (x2,y2).
    const hx = sk.x1 + (sk.x2 - sk.x1) * p;
    const hy = sk.y1 + (sk.y2 - sk.y1) * p;
    // Tail trails behind the head ~22% of the streak length.
    const tx = hx - (sk.x2 - sk.x1) * 0.22;
    const ty = hy - (sk.y2 - sk.y1) * 0.22;
    const grad = ctx.createLinearGradient(tx, ty, hx, hy);
    // Boosted streaks (Endless milestone fires) use a hotter cyan→white
    // glow + thicker line + larger head bulb so the meteor shower
    // reads as a deliberate "the sky is falling" beat rather than a
    // routine 8-streak garnish.
    const isBoost = !!sk.boosted;
    if (isBoost) {
      grad.addColorStop(0, "rgba(255,200,80,0)");
      grad.addColorStop(0.5, "rgba(255,160,80,0.7)");
      grad.addColorStop(0.85, "rgba(255,240,200,0.95)");
      grad.addColorStop(1, "rgba(255,255,255,1)");
    } else {
      grad.addColorStop(0, "rgba(255,140,66,0)");
      grad.addColorStop(0.6, "rgba(255,140,66,0.55)");
      grad.addColorStop(1, "rgba(255,255,255,0.95)");
    }
    ctx.strokeStyle = grad;
    ctx.lineWidth = isBoost ? 4.6 : 3.2;
    ctx.shadowColor = isBoost ? "#ffb84a" : "#ff8c42";
    ctx.shadowBlur = isBoost ? 22 : 14;
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(hx, hy);
    ctx.stroke();
    // Bright bulb at the head.
    ctx.shadowBlur = isBoost ? 26 : 18;
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.beginPath();
    ctx.arc(hx, hy, isBoost ? 4.5 : 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

export function drawParticles(ctx, s) {
  s.particles = s.particles.filter((p) => p.life > 0);
  s.particles.forEach((p) => {
    p.x += p.vx;
    p.y += p.vy;
    p.vy += 0.15;
    p.life -= 0.04;
    ctx.fillStyle = p.color + "cc";
    ctx.fillRect(p.x, p.y, 2.5, 2.5);
  });
}

export function drawSlowOverlay(ctx, s) {
  if (performance.now() < s.slowUntil) {
    ctx.fillStyle = "rgba(0,255,65,0.05)";
    ctx.fillRect(0, 0, W, H);
  }
}

// Short-lived black hole VFX drawn on top of the block field. `hole` looks
// like { x, y, startedAt, duration, maxRadius }.
export function drawBlackHole(ctx, hole) {
  const t = (performance.now() - hole.startedAt) / hole.duration;
  if (t >= 1 || t < 0) return false;
  // Shrink back in last 20% so it "collapses"
  const scale = t < 0.8 ? t / 0.8 : 1 - (t - 0.8) / 0.2;
  const r = hole.maxRadius * scale;

  ctx.save();
  // Deep void core
  const core = ctx.createRadialGradient(hole.x, hole.y, 0, hole.x, hole.y, r * 0.55);
  core.addColorStop(0, "#000000");
  core.addColorStop(1, "rgba(20,0,40,0.95)");
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.arc(hole.x, hole.y, r * 0.55, 0, Math.PI * 2);
  ctx.fill();

  // Accretion ring
  const ring = ctx.createRadialGradient(hole.x, hole.y, r * 0.55, hole.x, hole.y, r);
  ring.addColorStop(0, "rgba(107,61,245,0.95)");
  ring.addColorStop(0.5, "rgba(255,79,179,0.55)");
  ring.addColorStop(1, "rgba(176,38,255,0)");
  ctx.fillStyle = ring;
  ctx.globalCompositeOperation = "lighter";
  ctx.beginPath();
  ctx.arc(hole.x, hole.y, r, 0, Math.PI * 2);
  ctx.fill();

  // Swirl lines
  ctx.strokeStyle = "rgba(192,128,255,0.45)";
  ctx.lineWidth = 1.2;
  const spinPhase = (performance.now() - hole.startedAt) * 0.012;
  for (let i = 0; i < 4; i++) {
    const a = spinPhase + (i * Math.PI) / 2;
    ctx.beginPath();
    ctx.arc(hole.x, hole.y, r * (0.7 + 0.08 * i), a, a + Math.PI * 0.6);
    ctx.stroke();
  }
  ctx.restore();
  return true;
}


// Space Worm boss — purple space leviathan, redesigned Feb 2026 to match
// the new user-supplied reference: a hulking dark-violet predator with a
// gaping multi-row maw, glowing magenta cyclopean eye with neon-pink
// runes, curved horn-fin sweeping back from the skull, tail fin behind,
// and three trailing whiskers from the lower jaw. The leviathan faces
// RIGHT (maw on the +X side, eye + horn on the -X / "head" side).
// Drawn in model-space (radius `r`) and transformed via ctx.translate so
// the same routine renders both the small (r≈36) and giant (r≈60) tier.
//
// `worm` schema: { x, y, kind: 'small' | 'giant', headPhase, hitFlash,
// hp, maxHp }
export function drawSpaceWorm(ctx, worm) {
  // Skeleton-swarm mini-boss uses a completely different bone-white
  // silhouette — dispatch early so the rest of this function stays
  // focused on the angler-leviathan rendering.
  if (worm.kind === "skeleton") {
    return drawSkeletonWorm(ctx, worm);
  }
  // Reference: deep-sea anglerfish with a dark blue-teal body, huge
  // gaping mouth lined with interlocking white fangs over a deep RED
  // throat, two close-set ivory eyes high on the head, a thin curved
  // lure ending in a small teardrop bulb, dorsal spike crest, and a
  // bright CYAN/TEAL scalloped tail fan on the right.
  const r = worm.kind === "giant" ? 60 : 36;
  // Subtle vertical sway driven by the same headPhase clock the rest of
  // the engine uses, plus a horizontal lure-bob for life. Keep the body
  // rotation gentle so teeth don't visually slip relative to the maw.
  const sway = Math.sin(worm.headPhase) * 0.08;
  ctx.save();
  ctx.translate(worm.x, worm.y);
  ctx.rotate(sway);

  // --- Soft outer glow --------------------------------------------------
  // Cyan-teal corona to match the bioluminescent tail fan.
  const haloR = r * 1.7;
  const halo = ctx.createRadialGradient(0, 0, r * 0.3, 0, 0, haloR);
  halo.addColorStop(0, "rgba(120, 220, 220, 0.18)");
  halo.addColorStop(0.55, "rgba(40, 90, 110, 0.10)");
  halo.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(0, 0, haloR, 0, Math.PI * 2);
  ctx.fill();

  // Hit flash — magenta flare that lifts the whole silhouette on damage.
  if (worm.hitFlash > 0) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = Math.min(1, worm.hitFlash / 12);
    ctx.fillStyle = "#ff8eff";
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.15, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Breath cycle drives lure-bulb pulse, eye pulse, and throat glow so
  // the creature reads as one coordinated organism.
  const breath = 0.5 + 0.5 * Math.sin(worm.headPhase * 1.1);
  const roar = Math.max(0, worm.hitFlash || 0) / 12; // 0..1 spike

  // --- Caudal fin (real fish-style tail) -------------------------------
  // Replaces the previous 5-petal scallop with a single rounded fan
  // featuring 9 radiating fin rays, translucent dark teal membrane,
  // bright cyan ray streaks. Reads as a true caudal fin rather than a
  // flower. Drawn first so the body sits cleanly on top.
  const tailX = r * 0.92;
  const tailLen = r * 1.25;
  const tailHalf = r * 1.05;
  ctx.save();
  ctx.translate(tailX, 0);
  // Membrane silhouette — wide rounded crescent that flares back.
  const tailGrad = ctx.createLinearGradient(0, 0, tailLen, 0);
  tailGrad.addColorStop(0, "#0a1f24");
  tailGrad.addColorStop(0.55, "#1d6a73");
  tailGrad.addColorStop(0.85, "#3aa8a5");
  tailGrad.addColorStop(1, "#0d2f33");
  ctx.fillStyle = tailGrad;
  ctx.beginPath();
  ctx.moveTo(0, -r * 0.18);
  ctx.bezierCurveTo(
    tailLen * 0.45, -tailHalf * 0.95,
    tailLen * 0.85, -tailHalf * 1.05,
    tailLen, -tailHalf * 0.55,
  );
  // Slightly forked trailing edge — concave curve back to mid then out.
  ctx.bezierCurveTo(
    tailLen * 0.75, -tailHalf * 0.18,
    tailLen * 0.62, 0,
    tailLen * 0.75, tailHalf * 0.18,
  );
  ctx.bezierCurveTo(
    tailLen * 0.85, tailHalf * 1.05,
    tailLen * 0.45, tailHalf * 0.95,
    0, r * 0.18,
  );
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "rgba(6, 22, 26, 0.95)";
  ctx.lineWidth = Math.max(1, r * 0.03);
  ctx.stroke();
  // Fin rays — 9 thin cyan-tinted streaks fanning out.
  const rayCount = 9;
  ctx.strokeStyle = "rgba(140, 230, 220, 0.55)";
  ctx.lineWidth = Math.max(0.7, r * 0.022);
  for (let i = 0; i < rayCount; i++) {
    const t = i / (rayCount - 1);
    const ang = -0.85 + t * 1.7;
    const startR = r * 0.05;
    const endR = tailLen * (0.85 + 0.08 * Math.sin(t * Math.PI));
    ctx.beginPath();
    ctx.moveTo(Math.cos(ang) * startR, Math.sin(ang) * startR);
    ctx.lineTo(Math.cos(ang) * endR, Math.sin(ang) * endR);
    ctx.stroke();
  }
  // Bright cyan rim glow on the trailing edge.
  ctx.strokeStyle = "rgba(190, 255, 240, 0.45)";
  ctx.lineWidth = Math.max(0.8, r * 0.025);
  ctx.beginPath();
  ctx.moveTo(tailLen * 0.92, -tailHalf * 0.6);
  ctx.bezierCurveTo(
    tailLen * 0.78, -tailHalf * 0.18,
    tailLen * 0.78, tailHalf * 0.18,
    tailLen * 0.92, tailHalf * 0.6,
  );
  ctx.stroke();
  ctx.restore();

  // --- Body (organic anglerfish silhouette) ----------------------------
  // Origin at body center, mouth facing -x. Bulbous head tapering
  // smoothly to a slim caudal peduncle on the right. No more sawtooth
  // spike crest — replaced by 3 small soft dorsal spines for realism.
  const bodyW = r * 1.95;
  const bodyH = r * 1.55;
  const bodyGrad = ctx.createLinearGradient(0, -bodyH * 0.6, 0, bodyH * 0.6);
  bodyGrad.addColorStop(0, "#16242b");
  bodyGrad.addColorStop(0.5, "#0c181e");
  bodyGrad.addColorStop(1, "#050a0d");
  ctx.fillStyle = bodyGrad;
  ctx.strokeStyle = "rgba(8, 16, 22, 0.95)";
  ctx.lineWidth = Math.max(1, r * 0.03);
  ctx.beginPath();
  // Upper profile: mouth lip → forehead crown → smooth dorsal arc → tail peduncle.
  ctx.moveTo(-bodyW * 0.6, -bodyH * 0.05);
  ctx.bezierCurveTo(
    -bodyW * 0.62, -bodyH * 0.58,
    -bodyW * 0.28, -bodyH * 0.72,
    -bodyW * 0.06, -bodyH * 0.66,
  );
  // 3 small soft dorsal spines (no aggressive sawtooth).
  ctx.lineTo(-bodyW * 0.02, -bodyH * 0.78);  // spine 1 (tallest)
  ctx.lineTo(bodyW * 0.06, -bodyH * 0.6);
  ctx.lineTo(bodyW * 0.14, -bodyH * 0.7);    // spine 2
  ctx.lineTo(bodyW * 0.22, -bodyH * 0.55);
  ctx.lineTo(bodyW * 0.3, -bodyH * 0.62);    // spine 3
  ctx.bezierCurveTo(
    bodyW * 0.42, -bodyH * 0.4,
    bodyW * 0.5, -bodyH * 0.18,
    bodyW * 0.5, 0,                          // tail peduncle
  );
  // Lower profile: tail peduncle → soft anal-fin curve → belly → jaw.
  ctx.bezierCurveTo(
    bodyW * 0.5, bodyH * 0.18,
    bodyW * 0.42, bodyH * 0.4,
    bodyW * 0.32, bodyH * 0.5,
  );
  // Anal fin — smooth curved bump (no spikes).
  ctx.bezierCurveTo(
    bodyW * 0.22, bodyH * 0.7,
    bodyW * 0.08, bodyH * 0.7,
    -bodyW * 0.05, bodyH * 0.65,
  );
  ctx.bezierCurveTo(
    -bodyW * 0.28, bodyH * 0.78,
    -bodyW * 0.5, bodyH * 0.62,
    -bodyW * 0.55, bodyH * 0.45,
  );
  // Lower lip swing back up to the mouth front.
  ctx.lineTo(-bodyW * 0.6, bodyH * 0.05);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // --- Mottled skin texture --------------------------------------------
  // Scatter ~14 darker speckles + 6 lighter highlights across the upper
  // body for organic skin texture. Deterministic positions keyed off
  // body dimensions so the pattern doesn't strobe between frames.
  ctx.save();
  // Clip to body silhouette so spots don't bleed past the edge.
  ctx.beginPath();
  ctx.ellipse(-bodyW * 0.05, bodyH * 0.05, bodyW * 0.5, bodyH * 0.5, 0, 0, Math.PI * 2);
  ctx.clip();
  const speckles = [
    [-0.18, -0.32, 0.09], [-0.05, -0.45, 0.07], [0.12, -0.38, 0.08], [0.22, -0.2, 0.06],
    [0.05, -0.1, 0.07],  [-0.32, -0.18, 0.06], [0.32, -0.05, 0.08], [-0.1, 0.18, 0.07],
    [0.05, 0.32, 0.06],  [0.22, 0.22, 0.08],   [-0.22, 0.32, 0.06], [-0.04, 0.45, 0.05],
    [0.32, 0.35, 0.06],  [-0.16, -0.05, 0.05],
  ];
  ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
  for (const [px, py, sr] of speckles) {
    ctx.beginPath();
    ctx.arc(px * bodyW, py * bodyH, sr * r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Lighter highlight pebbles — top-back of body.
  const highlights = [
    [-0.12, -0.5, 0.05], [0.08, -0.52, 0.04], [0.2, -0.45, 0.05],
    [0.32, -0.32, 0.04], [-0.22, -0.45, 0.04], [0.4, -0.18, 0.04],
  ];
  ctx.fillStyle = "rgba(80, 130, 140, 0.35)";
  for (const [px, py, sr] of highlights) {
    ctx.beginPath();
    ctx.arc(px * bodyW, py * bodyH, sr * r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // --- Pectoral fin (leaf-shaped) --------------------------------------
  // Small fin near the gill, slightly translucent dark teal with 4
  // ray lines. Adds anatomical believability without competing for
  // attention with the maw or lure.
  ctx.save();
  ctx.translate(bodyW * 0.05, bodyH * 0.42);
  ctx.rotate(0.55);
  const finGrad = ctx.createLinearGradient(0, 0, r * 0.55, 0);
  finGrad.addColorStop(0, "rgba(20, 40, 50, 0.95)");
  finGrad.addColorStop(0.6, "rgba(50, 110, 120, 0.85)");
  finGrad.addColorStop(1, "rgba(20, 60, 70, 0.5)");
  ctx.fillStyle = finGrad;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(r * 0.1, -r * 0.22, r * 0.45, -r * 0.18, r * 0.55, 0);
  ctx.bezierCurveTo(r * 0.45, r * 0.18, r * 0.1, r * 0.22, 0, 0);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "rgba(8, 18, 24, 0.9)";
  ctx.lineWidth = Math.max(0.7, r * 0.022);
  ctx.stroke();
  // Fin rays
  ctx.strokeStyle = "rgba(120, 200, 200, 0.5)";
  ctx.lineWidth = Math.max(0.5, r * 0.015);
  for (let i = 0; i < 4; i++) {
    const a = -0.35 + (i / 3) * 0.7;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(a) * r * 0.5, Math.sin(a) * r * 0.5);
    ctx.stroke();
  }
  ctx.restore();

  // --- Gill slit (small, organic) --------------------------------------
  // Curved dark crescent on the cheek — much more realistic than the
  // previous bullseye-target shape. Reads as a real opercular slit.
  ctx.strokeStyle = "rgba(8, 16, 22, 0.85)";
  ctx.lineWidth = Math.max(0.9, r * 0.028);
  ctx.beginPath();
  ctx.moveTo(bodyW * 0.05, bodyH * 0.05);
  ctx.bezierCurveTo(
    bodyW * 0.08, bodyH * 0.18,
    bodyW * 0.15, bodyH * 0.32,
    bodyW * 0.22, bodyH * 0.4,
  );
  ctx.stroke();
  // Subtle inner slit shadow.
  ctx.strokeStyle = "rgba(60, 100, 110, 0.5)";
  ctx.lineWidth = Math.max(0.6, r * 0.018);
  ctx.beginPath();
  ctx.moveTo(bodyW * 0.07, bodyH * 0.08);
  ctx.bezierCurveTo(
    bodyW * 0.1, bodyH * 0.2,
    bodyW * 0.16, bodyH * 0.32,
    bodyW * 0.22, bodyH * 0.38,
  );
  ctx.stroke();

  // --- Maw cavity (carved into front of body) — DEEP RED throat -------
  // Asymmetric mouth shape — back corner curls up (the classic anglerfish
  // "grin"), front lip is more pronounced. Built from a custom bezier
  // path rather than a plain ellipse for a more organic predator look.
  const mouthCx = -bodyW * 0.34;
  const mouthW = bodyW * 0.58 * (1.0 + 0.4 * roar);
  const mouthH = bodyH * 0.85 * (1.0 + 0.22 * roar);
  const mouthCy = bodyH * 0.0;
  ctx.save();
  // Clip to a curved mouth silhouette: front-upper rises high (forehead
  // dip), back-upper curls up (smile), bottom is a smooth arc.
  ctx.beginPath();
  ctx.moveTo(mouthCx - mouthW * 0.5, mouthCy);                    // front corner
  ctx.bezierCurveTo(
    mouthCx - mouthW * 0.5, mouthCy - mouthH * 0.55,              // front lip rise
    mouthCx - mouthW * 0.15, mouthCy - mouthH * 0.5,
    mouthCx + mouthW * 0.05, mouthCy - mouthH * 0.45,             // upper roof
  );
  ctx.bezierCurveTo(
    mouthCx + mouthW * 0.35, mouthCy - mouthH * 0.42,
    mouthCx + mouthW * 0.5, mouthCy - mouthH * 0.32,              // back-upper curl up (grin)
    mouthCx + mouthW * 0.5, mouthCy - mouthH * 0.05,
  );
  // Bottom: smooth arc from back to front.
  ctx.bezierCurveTo(
    mouthCx + mouthW * 0.45, mouthCy + mouthH * 0.45,
    mouthCx + mouthW * 0.05, mouthCy + mouthH * 0.5,
    mouthCx - mouthW * 0.3, mouthCy + mouthH * 0.45,
  );
  ctx.bezierCurveTo(
    mouthCx - mouthW * 0.45, mouthCy + mouthH * 0.35,
    mouthCx - mouthW * 0.5, mouthCy + mouthH * 0.18,
    mouthCx - mouthW * 0.5, mouthCy,
  );
  ctx.closePath();
  ctx.clip();
  // Throat — saturated maroon→red→dark with breath pulse.
  const throatBright = 0.6 + 0.4 * breath + 0.2 * roar;
  const throat = ctx.createRadialGradient(mouthCx + mouthW * 0.1, mouthCy + mouthH * 0.1, mouthW * 0.04, mouthCx, mouthCy, mouthW * 0.55);
  throat.addColorStop(0, `rgba(${Math.round(195 * throatBright)}, ${Math.round(50 * throatBright)}, ${Math.round(50 * throatBright)}, 0.95)`);
  throat.addColorStop(0.5, "#5a1818");
  throat.addColorStop(1, "#180404");
  ctx.fillStyle = throat;
  ctx.fillRect(-bodyW, -bodyH, bodyW * 2, bodyH * 2);
  // Inner shadow on the front corner — gives depth, suggests the
  // throat narrowing back into the body.
  const innerShadow = ctx.createRadialGradient(
    mouthCx - mouthW * 0.4, mouthCy + mouthH * 0.05, mouthW * 0.05,
    mouthCx - mouthW * 0.4, mouthCy + mouthH * 0.05, mouthW * 0.45,
  );
  innerShadow.addColorStop(0, "rgba(0, 0, 0, 0.6)");
  innerShadow.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = innerShadow;
  ctx.fillRect(-bodyW, -bodyH, bodyW * 2, bodyH * 2);
  ctx.restore();
  // Lip outline — dark stroke just outside the mouth opening to define
  // it crisply against the body silhouette.
  ctx.strokeStyle = "rgba(8, 14, 20, 0.9)";
  ctx.lineWidth = Math.max(1, r * 0.03);
  ctx.beginPath();
  ctx.moveTo(mouthCx - mouthW * 0.5, mouthCy);
  ctx.bezierCurveTo(
    mouthCx - mouthW * 0.5, mouthCy - mouthH * 0.55,
    mouthCx - mouthW * 0.15, mouthCy - mouthH * 0.5,
    mouthCx + mouthW * 0.05, mouthCy - mouthH * 0.45,
  );
  ctx.bezierCurveTo(
    mouthCx + mouthW * 0.35, mouthCy - mouthH * 0.42,
    mouthCx + mouthW * 0.5, mouthCy - mouthH * 0.32,
    mouthCx + mouthW * 0.5, mouthCy - mouthH * 0.05,
  );
  ctx.bezierCurveTo(
    mouthCx + mouthW * 0.45, mouthCy + mouthH * 0.45,
    mouthCx + mouthW * 0.05, mouthCy + mouthH * 0.5,
    mouthCx - mouthW * 0.3, mouthCy + mouthH * 0.45,
  );
  ctx.bezierCurveTo(
    mouthCx - mouthW * 0.45, mouthCy + mouthH * 0.35,
    mouthCx - mouthW * 0.5, mouthCy + mouthH * 0.18,
    mouthCx - mouthW * 0.5, mouthCy,
  );
  ctx.closePath();
  ctx.stroke();

  // --- Teeth (long, curved, needle-like fangs) -------------------------
  // Real anglerfish have characteristically long, thin, slightly curved
  // fangs that interlock past the lips. We draw each tooth as a tapered
  // bezier-curved needle with an ivory-cream gradient (darker root,
  // translucent tip), variable lengths, and a subtle inward hook
  // toward the throat center. Front teeth are dramatically longer
  // (signature anglerfish "front fang" silhouette).
  const toothCount = worm.kind === "giant" ? 11 : 8;
  const throatCx = mouthCx;       // teeth curve toward the throat center
  const throatCy = bodyH * 0.05;
  const drawFang = (rootX, rootY, length, baseHalf, direction) => {
    // direction: +1 = upper jaw (tip below root), -1 = lower jaw.
    // Compute tip position with inward hook toward throat — front teeth
    // bend more, back teeth nearly straight.
    const dxToThroat = throatCx - rootX;
    const hook = Math.max(-0.55, Math.min(0.55, dxToThroat / (mouthW * 0.5)));
    const tipX = rootX + hook * length * 0.32;
    const tipY = rootY + direction * length;
    // Mid-curve control for organic curl.
    const midX = rootX + hook * length * 0.16;
    const midY = rootY + direction * length * 0.55;
    const sideHook = direction * 0.2;
    // Build the fang silhouette: root left → tip → root right via two
    // bezier curves so the tooth tapers smoothly to a sharp tip.
    ctx.beginPath();
    ctx.moveTo(rootX - baseHalf, rootY);
    ctx.bezierCurveTo(
      rootX - baseHalf * 0.6, midY - sideHook * baseHalf,
      midX - baseHalf * 0.25, midY,
      tipX, tipY,
    );
    ctx.bezierCurveTo(
      midX + baseHalf * 0.25, midY,
      rootX + baseHalf * 0.6, midY - sideHook * baseHalf,
      rootX + baseHalf, rootY,
    );
    ctx.closePath();
    // Ivory-cream gradient: opaque cream root → translucent ivory tip.
    const fg = ctx.createLinearGradient(rootX, rootY, tipX, tipY);
    fg.addColorStop(0, "#d8c89c");      // darker root
    fg.addColorStop(0.45, "#f3e7c8");
    fg.addColorStop(0.85, "#fdf6e2");
    fg.addColorStop(1, "rgba(255, 252, 235, 0.85)");  // translucent tip
    ctx.fillStyle = fg;
    ctx.fill();
    // Outline.
    ctx.strokeStyle = "rgba(40, 30, 20, 0.9)";
    ctx.lineWidth = Math.max(0.6, r * 0.018);
    ctx.stroke();
    // Center ridge highlight — very thin sliver of bright cream down
    // the spine of the tooth, gives it a 3D feel.
    ctx.strokeStyle = "rgba(255, 255, 240, 0.55)";
    ctx.lineWidth = Math.max(0.4, r * 0.01);
    ctx.beginPath();
    ctx.moveTo(rootX, rootY + direction * baseHalf * 0.4);
    ctx.bezierCurveTo(
      midX, midY,
      midX, midY,
      tipX, tipY,
    );
    ctx.stroke();
  };
  // Upper jaw — long varying fangs hanging from the upper lip into the
  // mouth cavity. Drop-shadow first so all fangs share one shadow pass.
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;
  for (let i = 0; i < toothCount; i++) {
    const t = (i + 0.5) / toothCount;
    const x = mouthCx - mouthW * 0.46 + t * mouthW * 0.92;
    const y = -mouthH * 0.4 + Math.sin(t * Math.PI) * mouthH * 0.08;
    // Variable length — front fangs much longer; staggered alternation.
    let lenMul = 1.0;
    if (i === 0) lenMul = 1.85;       // dramatic front fang
    else if (i === 1) lenMul = 0.65;
    else if (i === 2) lenMul = 1.55;  // second long fang
    else if (i === toothCount - 1) lenMul = 1.4;
    else lenMul = i % 2 === 0 ? 1.05 : 0.55;
    const len = r * (0.32 + 0.18 * Math.sin(t * Math.PI)) * lenMul;
    const baseHalf = r * (0.045 + 0.015 * lenMul);
    drawFang(x, y, len, baseHalf, 1);
  }
  ctx.restore();
  // Lower jaw — interlocks with upper, slightly shorter overall but
  // with its own staggered long fangs offset from the upper row.
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = -1;
  for (let i = 0; i < toothCount; i++) {
    const t = (i + 0.5) / toothCount;
    const x = mouthCx - mouthW * 0.42 + t * mouthW * 0.88;
    const y = mouthH * 0.4 - Math.sin(t * Math.PI) * mouthH * 0.08;
    let lenMul = 1.0;
    if (i === 1) lenMul = 1.7;        // long lower fang offset from upper
    else if (i === 2) lenMul = 0.6;
    else if (i === toothCount - 2) lenMul = 1.5;
    else lenMul = i % 2 === 1 ? 1.0 : 0.55;
    const len = r * (0.28 + 0.16 * Math.sin(t * Math.PI)) * lenMul;
    const baseHalf = r * (0.04 + 0.014 * lenMul);
    drawFang(x, y, len, baseHalf, -1);
  }
  ctx.restore();

  // --- Eyes (small pale-iris with dark pupil — more visible) ---------
  // Real anglerfish have small but distinct eyes: pale silvery-yellow
  // iris around a dark pupil with a bright catchlight reflecting their
  // own lure glow. Drawn slightly bulged from the head plane with a
  // subtle dark socket rim.
  const drawEye = (cx, cy, rad) => {
    // Socket rim — soft dark shadow under the eye to bed it into the head.
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.beginPath();
    ctx.arc(cx, cy + rad * 0.1, rad * 1.25, 0, Math.PI * 2);
    ctx.fill();
    // Eyeball — pale silvery-cream sphere with subtle gradient.
    const eyeGrad = ctx.createRadialGradient(
      cx - rad * 0.3, cy - rad * 0.3, rad * 0.1,
      cx, cy, rad,
    );
    eyeGrad.addColorStop(0, "#fefcef");
    eyeGrad.addColorStop(0.5, "#e8dfb8");
    eyeGrad.addColorStop(0.85, "#9c8e6a");
    eyeGrad.addColorStop(1, "#3d3622");
    ctx.fillStyle = eyeGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, Math.PI * 2);
    ctx.fill();
    // Iris — pale yellow-cream ring around the pupil.
    const irisGrad = ctx.createRadialGradient(cx, cy, rad * 0.1, cx, cy, rad * 0.65);
    irisGrad.addColorStop(0, "#3a2e16");
    irisGrad.addColorStop(0.35, "#d4b870");
    irisGrad.addColorStop(0.75, "#f5e3a0");
    irisGrad.addColorStop(1, "#7a6638");
    ctx.fillStyle = irisGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, rad * 0.65, 0, Math.PI * 2);
    ctx.fill();
    // Pupil — pure black slit-circle in the center.
    ctx.fillStyle = "#000000";
    ctx.beginPath();
    ctx.arc(cx, cy, rad * 0.32, 0, Math.PI * 2);
    ctx.fill();
    // Bright cyan-white catchlight — reflection of the lure.
    ctx.shadowBlur = 5 + 3 * breath;
    ctx.shadowColor = "rgba(200, 245, 255, 0.95)";
    ctx.fillStyle = `rgba(225, 250, 255, ${0.95 + 0.05 * breath})`;
    ctx.beginPath();
    ctx.arc(cx - rad * 0.18, cy - rad * 0.22, rad * 0.18, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    // Outline — thin dark rim around the whole eyeball.
    ctx.strokeStyle = "rgba(15, 20, 25, 0.9)";
    ctx.lineWidth = Math.max(0.7, rad * 0.08);
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, Math.PI * 2);
    ctx.stroke();
  };
  // Two eyes — front bigger, back smaller, both high on the head above
  // the lure-base plane. Larger and more forward than the previous
  // tiny-dot version for clear visibility at gameplay scale.
  drawEye(-bodyW * 0.22, -bodyH * 0.42, r * 0.2);   // front (bigger)
  drawEye(-bodyW * 0.04, -bodyH * 0.4, r * 0.16);   // back (smaller)

  // --- Lure antenna (long thin S-curve with teardrop bulb) -------------
  // Springs from the forehead, sweeps up-and-forward in a sharp angular
  // S-shape. Tip ends in a small ivory teardrop bulb that bobs gently.
  const lureBaseX = -bodyW * 0.06;
  const lureBaseY = -bodyH * 0.6;
  const lureSway = Math.sin(worm.headPhase * 0.9) * r * 0.06;
  const tipX = -bodyW * 1.1 + lureSway;
  const tipY = -bodyH * 0.05 - r * 0.05 * roar;
  // Arm — thin & sharp, bent at acute angles like the reference.
  ctx.strokeStyle = "#08111a";
  ctx.lineWidth = Math.max(2.2, r * 0.085);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(lureBaseX, lureBaseY);
  ctx.lineTo(-bodyW * 0.18, -bodyH * 1.25);   // sharp upward bend
  ctx.lineTo(-bodyW * 0.95, -bodyH * 1.15);   // long horizontal sweep
  ctx.lineTo(tipX, tipY);                       // sharp drop to bulb
  ctx.stroke();

  // Bulb — small ivory teardrop with a soft halo.
  const bulbR = r * (0.13 + 0.022 * breath);
  // Bioluminescent corona behind the bulb — bright cyan-white nimbus
  // that pulses with breath. Drawn as a separate radial gradient so
  // the glow is visible against both the body and the dark background.
  ctx.save();
  ctx.translate(tipX, tipY);
  const coronaR = bulbR * (3.2 + 0.4 * breath);
  const corona = ctx.createRadialGradient(0, 0, bulbR * 0.2, 0, 0, coronaR);
  corona.addColorStop(0, `rgba(220, 250, 255, ${0.55 + 0.2 * breath})`);
  corona.addColorStop(0.4, "rgba(140, 210, 230, 0.25)");
  corona.addColorStop(1, "rgba(40, 90, 110, 0)");
  ctx.fillStyle = corona;
  ctx.beginPath();
  ctx.arc(0, 0, coronaR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Thin dangling filaments below the bulb — 2 wispy strands giving the
  // lure its characteristic delicate prey-attractor look.
  ctx.save();
  ctx.translate(tipX, tipY);
  ctx.strokeStyle = "rgba(180, 220, 230, 0.6)";
  ctx.lineWidth = Math.max(0.5, r * 0.012);
  ctx.lineCap = "round";
  for (const fi of [-1, 1]) {
    const wig = Math.sin(worm.headPhase * 1.6 + fi) * r * 0.04;
    ctx.beginPath();
    ctx.moveTo(fi * bulbR * 0.35, bulbR * 0.4);
    ctx.bezierCurveTo(
      fi * bulbR * 0.5 + wig, bulbR * 1.3,
      fi * bulbR * 0.2 + wig, bulbR * 2.2,
      fi * bulbR * 0.35 + wig * 1.5, bulbR * 3.1,
    );
    ctx.stroke();
  }
  ctx.restore();

  ctx.shadowBlur = 14 + 8 * breath;
  ctx.shadowColor = "rgba(245, 235, 200, 0.95)";
  // Teardrop shape: ellipse pointed slightly up-right toward the arm tip.
  ctx.save();
  ctx.translate(tipX, tipY);
  ctx.rotate(-0.4);
  const bulbGrad = ctx.createRadialGradient(-bulbR * 0.25, -bulbR * 0.25, bulbR * 0.05, 0, 0, bulbR);
  bulbGrad.addColorStop(0, "#ffffff");
  bulbGrad.addColorStop(0.6, "#f1e6cd");
  bulbGrad.addColorStop(1, "#7a7060");
  ctx.fillStyle = bulbGrad;
  ctx.beginPath();
  ctx.ellipse(0, 0, bulbR * 0.85, bulbR * 1.1, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = "rgba(0,0,0,0.8)";
  ctx.lineWidth = Math.max(0.8, r * 0.018);
  ctx.beginPath();
  ctx.ellipse(0, 0, bulbR * 0.83, bulbR * 1.08, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  // --- HP bar -----------------------------------------------------------
  if (worm.hp < worm.maxHp) {
    const w = bodyW * 0.7;
    const h = 4;
    const y = bodyH * 0.78 + 8;
    const pct = Math.max(0, worm.hp / worm.maxHp);
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(-w / 2, y, w, h);
    ctx.fillStyle = pct > 0.5 ? "#f6efe1" : pct > 0.25 ? "#ffd28a" : "#ff5c7a";
    ctx.fillRect(-w / 2, y, w * pct, h);
    ctx.strokeStyle = "rgba(255,255,255,0.4)";
    ctx.lineWidth = 1;
    ctx.strokeRect(-w / 2, y, w, h);
  }

  ctx.restore();
}


// Bone-white skeleton-worm mini-boss. Visually distinct from the
// violet/magenta Space Leviathan: ivory body, hollow eye sockets,
// visible ribs, faint cyan phosphorescent halo. Drawn at half the
// silhouette size of a small Leviathan so a swarm of 3 reads as
// "fast, fragile, multiple targets" rather than "one big tank".
export function drawSkeletonWorm(ctx, worm) {
  const r = 22; // body radius — half of small Leviathan (32)
  const sway = Math.sin(worm.headPhase) * 0.18;
  ctx.save();
  ctx.translate(worm.x, worm.y);
  ctx.rotate(sway);

  // Phosphorescent halo (cyan) — sets the "haunted" mood.
  const haloR = r * 1.6;
  const halo = ctx.createRadialGradient(0, 0, r * 0.3, 0, 0, haloR);
  halo.addColorStop(0, "rgba(180, 235, 255, 0.32)");
  halo.addColorStop(0.6, "rgba(80, 130, 180, 0.12)");
  halo.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(0, 0, haloR, 0, Math.PI * 2);
  ctx.fill();

  // Hit flash — cyan instead of magenta to match the bone-cold theme.
  if (worm.hitFlash > 0) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = Math.min(1, worm.hitFlash / 12);
    ctx.fillStyle = "#aef0ff";
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.15, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Bony body — ivory ellipse with subtle blue-grey shading.
  const bodyW = r * 1.95;
  const bodyH = r * 1.25;
  const bodyGrad = ctx.createLinearGradient(-bodyW * 0.5, -bodyH * 0.5, bodyW * 0.5, bodyH * 0.5);
  bodyGrad.addColorStop(0, "#7c8a9c");
  bodyGrad.addColorStop(0.4, "#d8dde6");
  bodyGrad.addColorStop(0.7, "#f4f6fa");
  bodyGrad.addColorStop(1, "#9aa6b8");
  ctx.fillStyle = bodyGrad;
  ctx.beginPath();
  ctx.ellipse(0, 0, bodyW * 0.5, bodyH * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // Ribs — short dark vertical segments along the body. Reads as
  // a skeleton's spine when scanned at gameplay speed.
  ctx.strokeStyle = "rgba(60, 80, 100, 0.65)";
  ctx.lineWidth = Math.max(1, r * 0.08);
  for (let i = -2; i <= 2; i++) {
    const px = i * (bodyW * 0.16);
    ctx.beginPath();
    ctx.moveTo(px, -bodyH * 0.42);
    ctx.lineTo(px, bodyH * 0.42);
    ctx.stroke();
  }

  // Skull — slightly larger circular head on the right end.
  const skullR = r * 0.7;
  const skullCx = bodyW * 0.42;
  const skullGrad = ctx.createRadialGradient(skullCx - skullR * 0.3, -skullR * 0.2, skullR * 0.1, skullCx, 0, skullR);
  skullGrad.addColorStop(0, "#ffffff");
  skullGrad.addColorStop(0.6, "#dde3eb");
  skullGrad.addColorStop(1, "#8794a8");
  ctx.fillStyle = skullGrad;
  ctx.beginPath();
  ctx.arc(skullCx, 0, skullR, 0, Math.PI * 2);
  ctx.fill();

  // Hollow eye sockets — two black voids with a tiny inner cyan dot
  // that pulses with the breath cycle.
  const breath = 0.5 + 0.5 * Math.sin(worm.headPhase * 1.3);
  const eyeR = skullR * 0.28;
  for (const side of [-1, 1]) {
    const ex = skullCx + side * skullR * 0.3;
    const ey = -skullR * 0.18;
    ctx.fillStyle = "#0a0e14";
    ctx.beginPath();
    ctx.arc(ex, ey, eyeR, 0, Math.PI * 2);
    ctx.fill();
    // Inner pupil
    ctx.fillStyle = `rgba(0, 220, 255, ${0.5 + 0.5 * breath})`;
    ctx.beginPath();
    ctx.arc(ex, ey, eyeR * 0.45, 0, Math.PI * 2);
    ctx.fill();
  }

  // Jaw — small dark slit beneath the eyes.
  ctx.strokeStyle = "rgba(40, 50, 60, 0.85)";
  ctx.lineWidth = Math.max(1, r * 0.1);
  ctx.beginPath();
  ctx.moveTo(skullCx - skullR * 0.45, skullR * 0.25);
  ctx.lineTo(skullCx + skullR * 0.55, skullR * 0.3);
  ctx.stroke();

  // Tiny tail flick on the back-left.
  ctx.fillStyle = "#bcc4d2";
  ctx.beginPath();
  ctx.moveTo(-bodyW * 0.5, 0);
  ctx.lineTo(-bodyW * 0.72, -bodyH * 0.22);
  ctx.lineTo(-bodyW * 0.6, 0);
  ctx.lineTo(-bodyW * 0.72, bodyH * 0.22);
  ctx.closePath();
  ctx.fill();

  // HP bar — small white pip strip above the skull, only when damaged.
  if (worm.hp < worm.maxHp) {
    const w = bodyW * 0.7;
    const y = -bodyH * 0.85;
    const h = 3;
    const pct = Math.max(0, worm.hp / worm.maxHp);
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(-w / 2, y, w, h);
    ctx.fillStyle = "#aef0ff";
    ctx.fillRect(-w / 2, y, w * pct, h);
  }

  ctx.restore();
}
